import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  Tooltip,
  CircularProgress,
  Autocomplete,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Sensors as SensorsIcon,
  PrecisionManufacturing as MachineIcon,
  Factory as FactoryIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  Manufacturer,
  MachineType,
  Sensor,
  getManufacturers,
  getMachineTypes,
  getSensors,
  getSensorTypes,
  getCategories,
  createManufacturer,
  updateManufacturer,
  deleteManufacturer,
  createMachineType,
  updateMachineType,
  deleteMachineType,
  createSensor,
  updateSensor,
  deleteSensor,
} from '../services/backendApi';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

// Common sensor types for autocomplete
const commonSensorTypes = [
  'Temperature', 'Pressure', 'Flow', 'Level', 'Vibration', 'Current', 'Voltage',
  'Power', 'Speed', 'Position', 'Torque', 'Force', 'Acceleration', 'Humidity',
  'Load', 'Counter', 'Time', 'Frequency', 'Density', 'pH', 'Conductivity',
];

// Common units for autocomplete
const commonUnits = [
  '°C', '°F', 'K', 'bar', 'psi', 'Pa', 'kPa', 'MPa', 'mbar',
  'l/min', 'm³/h', 'kg/h', 'g/s', 'm³/s',
  'mm', 'cm', 'm', 'µm', 'mm/s', 'm/s', 'km/h', 'rpm',
  'A', 'mA', 'V', 'mV', 'kV', 'W', 'kW', 'MW', 'VA', 'kVA',
  'Nm', 'kNm', 'N', 'kN', 'kg', 'g', 't',
  '%', 'Hz', 'kHz', 'MHz', 'ms', 's', 'min', 'h',
  'mm/s²', 'm/s²', 'g', 'dB', 'dB(A)',
  'kg/m³', 'g/l', 'µS/cm', 'mS/cm', 'pH',
  'pulses', 'cycles', 'rev', 'counts',
];

// Common protocols for autocomplete
const commonProtocols = [
  'OPC UA', 'MQTT', 'Modbus TCP', 'Modbus RTU', 'PROFINET', 'PROFIBUS',
  'EtherNet/IP', 'EtherCAT', 'CANopen', 'DeviceNet', 'IO-Link', 'HART',
  'FOUNDATION Fieldbus', 'CC-Link', 'Powerlink', 'SERCOS III', 'BACnet',
];

// Common categories for autocomplete
const commonCategories = [
  'PLC', 'Robot', 'Cobot', 'CNC', 'Drive', 'Motor', 'Sensor', 'HMI',
  'Controller', 'IO', 'Gateway', 'Switch', 'Encoder', 'Actuator',
  'Pump', 'Valve', 'Compressor', 'Conveyor', 'Welder', 'Vision',
];

interface EquipmentManagerProps {
  open: boolean;
  onClose: () => void;
}

const EquipmentManager: React.FC<EquipmentManagerProps> = ({ open, onClose }) => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Data states
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);
  const [machineTypes, setMachineTypes] = useState<MachineType[]>([]);
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [sensorTypes, setSensorTypes] = useState<string[]>(commonSensorTypes);
  const [categories, setCategories] = useState<string[]>(commonCategories);

  // Dialog states
  const [manufacturerDialog, setManufacturerDialog] = useState(false);
  const [machineDialog, setMachineDialog] = useState(false);
  const [sensorDialog, setSensorDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; type: string; id: string; name: string }>({
    open: false,
    type: '',
    id: '',
    name: '',
  });

  // Form states
  const [editingManufacturer, setEditingManufacturer] = useState<Partial<Manufacturer> | null>(null);
  const [editingMachine, setEditingMachine] = useState<Partial<MachineType> | null>(null);
  const [editingSensor, setEditingSensor] = useState<Partial<Sensor> | null>(null);

  // Filter states
  const [selectedManufacturer, setSelectedManufacturer] = useState<string>('');
  const [selectedMachineType, setSelectedMachineType] = useState<string>('');

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [manuData, catData, sensorTypeData] = await Promise.all([
        getManufacturers(),
        getCategories(),
        getSensorTypes(),
      ]);
      setManufacturers(manuData);
      setCategories(Array.from(new Set([...commonCategories, ...catData])));
      setSensorTypes(Array.from(new Set([...commonSensorTypes, ...sensorTypeData])));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMachineTypes = useCallback(async () => {
    try {
      const data = await getMachineTypes(selectedManufacturer || undefined);
      setMachineTypes(data);
    } catch (error) {
      console.error('Error loading machine types:', error);
    }
  }, [selectedManufacturer]);

  const loadSensors = useCallback(async () => {
    try {
      const data = await getSensors(selectedMachineType || undefined);
      setSensors(data);
    } catch (error) {
      console.error('Error loading sensors:', error);
    }
  }, [selectedMachineType]);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, loadData]);

  useEffect(() => {
    if (tabValue === 1) {
      loadMachineTypes();
    }
  }, [tabValue, selectedManufacturer, loadMachineTypes]);

  useEffect(() => {
    if (tabValue === 2) {
      loadSensors();
    }
  }, [tabValue, selectedMachineType, loadSensors]);

  // Handlers
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // Manufacturer handlers
  const handleSaveManufacturer = async () => {
    if (!editingManufacturer?.name) {
      showSnackbar('Name ist erforderlich', 'error');
      return;
    }

    try {
      if (editingManufacturer.id) {
        await updateManufacturer(editingManufacturer.id, editingManufacturer);
        showSnackbar('Hersteller aktualisiert', 'success');
      } else {
        await createManufacturer(editingManufacturer as { name: string });
        showSnackbar('Hersteller erstellt', 'success');
      }
      setManufacturerDialog(false);
      setEditingManufacturer(null);
      loadData();
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Speichern';
      showSnackbar(errorMessage, 'error');
    }
  };

  // Machine type handlers
  const handleSaveMachine = async () => {
    if (!editingMachine?.manufacturer_id || !editingMachine?.name) {
      showSnackbar('Hersteller und Name sind erforderlich', 'error');
      return;
    }

    try {
      if (editingMachine.id) {
        await updateMachineType(editingMachine.id, editingMachine);
        showSnackbar('Maschinentyp aktualisiert', 'success');
      } else {
        await createMachineType(editingMachine as { manufacturer_id: string; name: string });
        showSnackbar('Maschinentyp erstellt', 'success');
      }
      setMachineDialog(false);
      setEditingMachine(null);
      loadMachineTypes();
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Speichern';
      showSnackbar(errorMessage, 'error');
    }
  };

  // Sensor handlers
  const handleSaveSensor = async () => {
    if (!editingSensor?.machine_type_id || !editingSensor?.name || !editingSensor?.sensor_type || !editingSensor?.unit) {
      showSnackbar('Maschine, Name, Sensortyp und Einheit sind erforderlich', 'error');
      return;
    }

    try {
      if (editingSensor.id) {
        await updateSensor(editingSensor.id, editingSensor);
        showSnackbar('Sensor aktualisiert', 'success');
      } else {
        await createSensor(editingSensor as { machine_type_id: string; name: string; sensor_type: string; unit: string });
        showSnackbar('Sensor erstellt', 'success');
      }
      setSensorDialog(false);
      setEditingSensor(null);
      loadSensors();
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Speichern';
      showSnackbar(errorMessage, 'error');
    }
  };

  // Delete handler
  const handleDelete = async () => {
    try {
      switch (deleteDialog.type) {
        case 'manufacturer':
          await deleteManufacturer(deleteDialog.id);
          loadData();
          break;
        case 'machine':
          await deleteMachineType(deleteDialog.id);
          loadMachineTypes();
          break;
        case 'sensor':
          await deleteSensor(deleteDialog.id);
          loadSensors();
          break;
      }
      showSnackbar('Erfolgreich gelöscht', 'success');
    } catch (error) {
      showSnackbar('Fehler beim Löschen', 'error');
    }
    setDeleteDialog({ open: false, type: '', id: '', name: '' });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="span">
          ⚙️ Equipment-Verwaltung
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab icon={<FactoryIcon />} label="Hersteller" />
            <Tab icon={<MachineIcon />} label="Maschinentypen" />
            <Tab icon={<SensorsIcon />} label="Sensoren" />
          </Tabs>
        </Box>

        {/* Manufacturers Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingManufacturer({});
                setManufacturerDialog(true);
              }}
            >
              Neuer Hersteller
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Land</TableCell>
                  <TableCell>Website</TableCell>
                  <TableCell>Beschreibung</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {manufacturers.map((m) => (
                  <TableRow key={m.id} hover>
                    <TableCell><strong>{m.name}</strong></TableCell>
                    <TableCell>{m.country}</TableCell>
                    <TableCell>
                      {m.website && (
                        <a href={m.website} target="_blank" rel="noopener noreferrer">
                          {m.website.replace(/https?:\/\//, '')}
                        </a>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.description}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Bearbeiten">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingManufacturer(m);
                            setManufacturerDialog(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Löschen">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, type: 'manufacturer', id: m.id, name: m.name })}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Machine Types Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2 }}>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>Nach Hersteller filtern</InputLabel>
              <Select
                value={selectedManufacturer}
                label="Nach Hersteller filtern"
                onChange={(e) => setSelectedManufacturer(e.target.value)}
              >
                <MenuItem value="">Alle Hersteller</MenuItem>
                {manufacturers.map((m) => (
                  <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingMachine({ manufacturer_id: selectedManufacturer || '' });
                setMachineDialog(true);
              }}
            >
              Neuer Maschinentyp
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Hersteller</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Modell</TableCell>
                  <TableCell>Kategorie</TableCell>
                  <TableCell>Protokolle</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {machineTypes.map((mt) => (
                  <TableRow key={mt.id} hover>
                    <TableCell>{mt.manufacturer_name}</TableCell>
                    <TableCell><strong>{mt.name}</strong></TableCell>
                    <TableCell>{mt.model}</TableCell>
                    <TableCell>
                      <Chip label={mt.category} size="small" />
                    </TableCell>
                    <TableCell>
                      {mt.communication_protocols?.slice(0, 3).map((p) => (
                        <Chip key={p} label={p} size="small" sx={{ mr: 0.5, mb: 0.5 }} variant="outlined" />
                      ))}
                      {mt.communication_protocols?.length > 3 && (
                        <Chip label={`+${mt.communication_protocols.length - 3}`} size="small" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Bearbeiten">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingMachine(mt);
                            setMachineDialog(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Löschen">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, type: 'machine', id: mt.id, name: mt.name })}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Sensors Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 2 }}>
            <FormControl sx={{ minWidth: 300 }}>
              <InputLabel>Nach Maschinentyp filtern</InputLabel>
              <Select
                value={selectedMachineType}
                label="Nach Maschinentyp filtern"
                onChange={(e) => setSelectedMachineType(e.target.value)}
              >
                <MenuItem value="">Alle Maschinen</MenuItem>
                {machineTypes.map((mt) => (
                  <MenuItem key={mt.id} value={mt.id}>{mt.manufacturer_name} - {mt.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingSensor({ machine_type_id: selectedMachineType || '' });
                setSensorDialog(true);
              }}
            >
              Neuer Sensor
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Typ</TableCell>
                  <TableCell>Einheit</TableCell>
                  <TableCell>Bereich</TableCell>
                  <TableCell>OPC UA Node</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sensors.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell><strong>{s.name}</strong></TableCell>
                    <TableCell>
                      <Chip label={s.sensor_type} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>{s.unit}</TableCell>
                    <TableCell>
                      {s.min_value !== null && s.max_value !== null
                        ? `${s.min_value} - ${s.max_value} ${s.unit}`
                        : '-'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {s.opc_ua_node_id}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Bearbeiten">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingSensor(s);
                            setSensorDialog(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Löschen">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, type: 'sensor', id: s.id, name: s.name })}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </DialogContent>

      {/* Manufacturer Dialog */}
      <Dialog open={manufacturerDialog} onClose={() => setManufacturerDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingManufacturer?.id ? 'Hersteller bearbeiten' : 'Neuer Hersteller'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Name *"
              value={editingManufacturer?.name || ''}
              onChange={(e) => setEditingManufacturer({ ...editingManufacturer, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Land"
              value={editingManufacturer?.country || ''}
              onChange={(e) => setEditingManufacturer({ ...editingManufacturer, country: e.target.value })}
              fullWidth
            />
            <TextField
              label="Website"
              value={editingManufacturer?.website || ''}
              onChange={(e) => setEditingManufacturer({ ...editingManufacturer, website: e.target.value })}
              fullWidth
              placeholder="https://example.com"
            />
            <TextField
              label="Beschreibung"
              value={editingManufacturer?.description || ''}
              onChange={(e) => setEditingManufacturer({ ...editingManufacturer, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManufacturerDialog(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSaveManufacturer}>Speichern</Button>
        </DialogActions>
      </Dialog>

      {/* Machine Type Dialog */}
      <Dialog open={machineDialog} onClose={() => setMachineDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingMachine?.id ? 'Maschinentyp bearbeiten' : 'Neuer Maschinentyp'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 2 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Hersteller *</InputLabel>
                <Select
                  value={editingMachine?.manufacturer_id || ''}
                  label="Hersteller *"
                  onChange={(e) => setEditingMachine({ ...editingMachine, manufacturer_id: e.target.value as string })}
                >
                  {manufacturers.map((m) => (
                    <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Name *"
                value={editingMachine?.name || ''}
                onChange={(e) => setEditingMachine({ ...editingMachine, name: e.target.value })}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Modell"
                value={editingMachine?.model || ''}
                onChange={(e) => setEditingMachine({ ...editingMachine, model: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                freeSolo
                options={categories}
                value={editingMachine?.category || ''}
                onInputChange={(_, value) => setEditingMachine({ ...editingMachine, category: value })}
                renderInput={(params) => <TextField {...params} label="Kategorie" />}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                multiple
                freeSolo
                options={commonProtocols}
                value={editingMachine?.communication_protocols || []}
                onChange={(_, value) => setEditingMachine({ ...editingMachine, communication_protocols: value })}
                renderInput={(params) => <TextField {...params} label="Kommunikationsprotokolle" />}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} key={option} />
                  ))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Beschreibung"
                value={editingMachine?.description || ''}
                onChange={(e) => setEditingMachine({ ...editingMachine, description: e.target.value })}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMachineDialog(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSaveMachine}>Speichern</Button>
        </DialogActions>
      </Dialog>

      {/* Sensor Dialog */}
      <Dialog open={sensorDialog} onClose={() => setSensorDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSensor?.id ? 'Sensor bearbeiten' : 'Neuer Sensor'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 2 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Maschinentyp *</InputLabel>
                <Select
                  value={editingSensor?.machine_type_id || ''}
                  label="Maschinentyp *"
                  onChange={(e) => setEditingSensor({ ...editingSensor, machine_type_id: e.target.value as string })}
                >
                  {machineTypes.map((mt) => (
                    <MenuItem key={mt.id} value={mt.id}>{mt.manufacturer_name} - {mt.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Name *"
                value={editingSensor?.name || ''}
                onChange={(e) => setEditingSensor({ ...editingSensor, name: e.target.value })}
                fullWidth
                required
                placeholder="z.B. Motor_Temperature"
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                freeSolo
                options={sensorTypes}
                value={editingSensor?.sensor_type || ''}
                onInputChange={(_, value) => setEditingSensor({ ...editingSensor, sensor_type: value })}
                renderInput={(params) => <TextField {...params} label="Sensortyp *" required />}
              />
            </Grid>
            <Grid item xs={3}>
              <Autocomplete
                freeSolo
                options={commonUnits}
                value={editingSensor?.unit || ''}
                onInputChange={(_, value) => setEditingSensor({ ...editingSensor, unit: value })}
                renderInput={(params) => <TextField {...params} label="Einheit *" required />}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label="Min-Wert"
                type="number"
                value={editingSensor?.min_value ?? ''}
                onChange={(e) => setEditingSensor({ ...editingSensor, min_value: parseFloat(e.target.value) || 0 })}
                fullWidth
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label="Max-Wert"
                type="number"
                value={editingSensor?.max_value ?? ''}
                onChange={(e) => setEditingSensor({ ...editingSensor, max_value: parseFloat(e.target.value) || 0 })}
                fullWidth
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label="Typischer Wert"
                type="number"
                value={editingSensor?.typical_value ?? ''}
                onChange={(e) => setEditingSensor({ ...editingSensor, typical_value: parseFloat(e.target.value) || 0 })}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Präzision (Nachkommastellen)"
                type="number"
                value={editingSensor?.precision_digits ?? 2}
                onChange={(e) => setEditingSensor({ ...editingSensor, precision_digits: parseInt(e.target.value) || 2 })}
                fullWidth
                inputProps={{ min: 0, max: 10 }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Abtastrate (Hz)"
                type="number"
                value={editingSensor?.sampling_rate_hz ?? 1}
                onChange={(e) => setEditingSensor({ ...editingSensor, sampling_rate_hz: parseFloat(e.target.value) || 1 })}
                fullWidth
                inputProps={{ min: 0.001, step: 0.1 }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="OPC UA Node ID"
                value={editingSensor?.opc_ua_node_id || ''}
                onChange={(e) => setEditingSensor({ ...editingSensor, opc_ua_node_id: e.target.value })}
                fullWidth
                placeholder="ns=2;s=Sensor.Value"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Beschreibung"
                value={editingSensor?.description || ''}
                onChange={(e) => setEditingSensor({ ...editingSensor, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSensorDialog(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSaveSensor}>Speichern</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ ...deleteDialog, open: false })}>
        <DialogTitle>Löschen bestätigen</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Möchten Sie <strong>{deleteDialog.name}</strong> wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
          </Alert>
          {deleteDialog.type === 'manufacturer' && (
            <Typography variant="body2" color="text.secondary">
              ⚠️ Alle zugehörigen Maschinentypen und Sensoren werden ebenfalls gelöscht!
            </Typography>
          )}
          {deleteDialog.type === 'machine' && (
            <Typography variant="body2" color="text.secondary">
              ⚠️ Alle zugehörigen Sensoren werden ebenfalls gelöscht!
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ ...deleteDialog, open: false })}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Löschen</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default EquipmentManager;
